import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Code2, ExternalLink, Loader2 } from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalMe } from "@/lib/portalApi";

const DEFAULT_ACCENT = "D9A6F5";

export default function PortalEmbed() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const [theme, setTheme] = useState("dark");
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [view, setView] = useState("grid");
  const [limit, setLimit] = useState(6);
  const [transparent, setTransparent] = useState(false);
  const [height, setHeight] = useState(720);

  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await portalMe();
        setMe(res);
      } catch {
        /* handled */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const slug = me?.venue?.slug;

  const embedUrl = useMemo(() => {
    if (!slug) return "";
    const origin = window.location.origin;
    const q = new URLSearchParams({
      theme,
      accent: accent.replace(/^#/, ""),
      view,
      limit: String(limit),
    });
    if (transparent) q.set("bg", "transparent");
    return `${origin}/embed/v/${slug}?${q.toString()}`;
  }, [slug, theme, accent, view, limit, transparent]);

  const iframeSnippet = useMemo(() => {
    if (!embedUrl) return "";
    return `<iframe
  src="${embedUrl}"
  width="100%"
  height="${height}"
  style="border:0;width:100%;"
  loading="lazy"
  title="What's on"
  allow="fullscreen"
></iframe>`;
  }, [embedUrl, height]);

  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setter(false), 1800);
    } catch {
      toast.error("Copy failed — please select and copy manually.");
    }
  };

  return (
    <PortalShell me={me} testId="portal-embed-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
            Website embed
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-3">
            Put your calendar on your website
          </h1>
          <p className="text-[#A3A3A3] max-w-2xl">
            Copy the snippet and paste it into your existing website. Your upcoming events will
            appear automatically — no updates required. When you add or edit an event in Swingle,
            it changes on your site instantly.
          </p>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : !slug ? (
          <div className="text-[#A3A3A3]">No venue attached to your account yet.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
            {/* Controls */}
            <aside className="space-y-6">
              <Section title="Theme">
                <RadioRow
                  value={theme}
                  onChange={setTheme}
                  options={[
                    { v: "dark", label: "Dark" },
                    { v: "light", label: "Light" },
                  ]}
                  testId="embed-theme"
                />
              </Section>

              <Section title="Layout">
                <RadioRow
                  value={view}
                  onChange={setView}
                  options={[
                    { v: "grid", label: "Grid" },
                    { v: "list", label: "List" },
                  ]}
                  testId="embed-view"
                />
              </Section>

              <Section title="Accent colour">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={`#${accent.replace(/^#/, "")}`}
                    onChange={(e) => setAccent(e.target.value.replace(/^#/, "").toUpperCase())}
                    data-testid="embed-accent"
                    className="h-11 w-16 bg-black border border-white/15 p-1 cursor-pointer"
                    aria-label="Accent colour"
                  />
                  <input
                    type="text"
                    value={`#${accent.replace(/^#/, "").toUpperCase()}`}
                    onChange={(e) =>
                      setAccent(e.target.value.replace(/^#/, "").toUpperCase().slice(0, 6))
                    }
                    className="flex-1 bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] uppercase tracking-widest font-mono text-sm focus:outline-none focus:border-[#D9A6F5]"
                    maxLength={7}
                  />
                </div>
              </Section>

              <Section title="Events to show">
                <input
                  type="number"
                  min={1}
                  max={24}
                  value={limit}
                  data-testid="embed-limit"
                  onChange={(e) =>
                    setLimit(Math.max(1, Math.min(24, parseInt(e.target.value || "6", 10) || 6)))
                  }
                  className="w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
                />
              </Section>

              <Section title="Frame height (px)">
                <input
                  type="number"
                  min={200}
                  max={2400}
                  step={20}
                  value={height}
                  onChange={(e) =>
                    setHeight(Math.max(200, Math.min(2400, parseInt(e.target.value || "720", 10) || 720)))
                  }
                  className="w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
                />
              </Section>

              <Section title="Background">
                <label className="flex items-center gap-3 cursor-pointer select-none text-sm text-[#F5F5F0]">
                  <input
                    type="checkbox"
                    checked={transparent}
                    onChange={(e) => setTransparent(e.target.checked)}
                    data-testid="embed-transparent"
                    className="h-4 w-4 accent-[#D9A6F5]"
                  />
                  Transparent (inherit from your page)
                </label>
              </Section>
            </aside>

            {/* Snippet + Preview */}
            <div className="space-y-8">
              {/* Snippet */}
              <div className="border border-white/10 bg-[#0A0A0A]">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
                    <Code2 size={13} /> Embed snippet
                  </div>
                  <button
                    type="button"
                    data-testid="embed-copy-snippet"
                    onClick={() => copy(iframeSnippet, setCopiedSnippet)}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-black bg-[#D9A6F5] hover:bg-[#E8C8FF] px-4 py-2 transition-colors"
                  >
                    {copiedSnippet ? <Check size={13} /> : <Copy size={13} />}
                    {copiedSnippet ? "Copied" : "Copy"}
                  </button>
                </div>
                <pre className="p-5 text-xs md:text-sm text-[#F5F5F0] overflow-x-auto whitespace-pre font-mono leading-relaxed">
                  {iframeSnippet}
                </pre>
              </div>

              {/* Direct link */}
              <div className="border border-white/10 bg-[#0A0A0A]">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
                    <ExternalLink size={13} /> Direct link
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={embedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
                    >
                      Open <ExternalLink size={12} />
                    </a>
                    <button
                      type="button"
                      data-testid="embed-copy-url"
                      onClick={() => copy(embedUrl, setCopiedUrl)}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#F5F5F0] border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-3 py-2 transition-colors"
                    >
                      {copiedUrl ? <Check size={12} /> : <Copy size={12} />}
                      {copiedUrl ? "Copied" : "Copy URL"}
                    </button>
                  </div>
                </div>
                <div className="p-5 text-xs md:text-sm text-[#A3A3A3] break-all font-mono">
                  {embedUrl}
                </div>
              </div>

              {/* Preview */}
              <div className="border border-white/10 bg-[#0A0A0A]">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                  <div className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
                    Live preview
                  </div>
                  <div className="text-xs text-[#A3A3A3]">Updates automatically</div>
                </div>
                <div className="p-4 bg-[#050505]">
                  <iframe
                    key={embedUrl + "-" + height}
                    src={embedUrl}
                    title="Embed preview"
                    data-testid="embed-preview-iframe"
                    style={{
                      width: "100%",
                      height: `${height}px`,
                      border: 0,
                      background: transparent ? "transparent" : "#0A0A0A",
                    }}
                    loading="lazy"
                  />
                </div>
              </div>

              <p className="text-xs text-[#A3A3A3] leading-relaxed">
                Paste the snippet into any HTML block — WordPress (Custom HTML), Wix (Embed
                Code), Squarespace (Code Block), Webflow (Embed), Shopify, Framer, or a bespoke
                site. Events refresh live from Swingle; you never need to update your website
                when your schedule changes.
              </p>
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-2">{title}</div>
      {children}
    </div>
  );
}

function RadioRow({ value, onChange, options, testId }) {
  return (
    <div className="grid grid-cols-2 border border-white/15" data-testid={testId}>
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            data-testid={`${testId}-${o.v}`}
            onClick={() => onChange(o.v)}
            className={`h-11 text-xs uppercase tracking-[0.22em] font-bold transition-colors ${
              active
                ? "bg-[#D9A6F5] text-black"
                : "bg-black text-[#A3A3A3] hover:text-[#F5F5F0]"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
